import useUpdateAddressPropertyInfo from '@/api/mutations/addresses/useUpdateAddressPropertyInfo';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Form, Modal } from '@payaca/ambrosia';
import { FC, ReactNode, useState } from 'react';
import { useForm } from 'react-hook-form';
import { PropertyInfoFields } from './PropertyInfoFields';
import {
  buildUpdateAddressPropertyInfoInput,
  propertyInfoDefaultValues,
  PropertyInfoSeed,
} from './propertyInfoFormHelpers';
import {
  buildPropertyInfoSchema,
  PropertyInfoFormValues,
  PropertyInfoVariant,
} from './propertyInfoSchema';

/**
 * Outcome of the `onSaved` callback. Return `{ error }` to surface a known
 * error message in the form; return `undefined` (or nothing) on success.
 */
export type PropertyInfoSaveResult = { error: string } | undefined;

type PropertyInfoFormProps = {
  addressId: string;
  /** Existing property info used to seed the form. */
  propertyInfo?: PropertyInfoSeed;
  /** Label for the submit button. */
  submitLabel: string;
  /** Which context the form is used in; drives field visibility and validation. */
  variant: PropertyInfoVariant;
  /** Optional notice shown as an alert at the top of the form. */
  notice?: ReactNode;
  /**
   * Runs after the property info is saved. Any downstream action (toast,
   * closing the modal, a chained mutation) belongs here. Return `{ error }`
   * to surface that message as a form-level error; an unexpected thrown error
   * shows 'Something went wrong'.
   */
  onSaved: () => Promise<PropertyInfoSaveResult> | PropertyInfoSaveResult;
};

/**
 * Captures property details and persists them to an address via
 * `updateAddressPropertyInfo`. Renders as modal body + footer, so it must be
 * used inside an ambrosia `Modal`.
 */
export const PropertyInfoForm: FC<PropertyInfoFormProps> = (props) => {
  const { addressId, propertyInfo, submitLabel, variant, notice, onSaved } =
    props;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Tracks the post-save `onSaved` phase (e.g. a chained Spruce push), which is
  // external async work not covered by the save mutation's own pending state.
  const [isRunningOnSaved, setIsRunningOnSaved] = useState(false);

  const { mutate: updatePropertyInfo, isPending: isUpdating } =
    useUpdateAddressPropertyInfo({ meta: { skipGlobalErrorToast: true } });

  const formMethods = useForm<PropertyInfoFormValues>({
    mode: 'all',
    resolver: zodResolver(buildPropertyInfoSchema(variant)),
    defaultValues: propertyInfoDefaultValues(propertyInfo),
  });

  const isProcessing = isUpdating || isRunningOnSaved;

  const runOnSaved = async () => {
    setIsRunningOnSaved(true);
    try {
      const result = await onSaved();
      if (result) {
        setErrorMessage(result.error);
      }
    } catch {
      setErrorMessage('Something went wrong');
    } finally {
      setIsRunningOnSaved(false);
    }
  };

  const handleSubmit = (data: PropertyInfoFormValues) => {
    setErrorMessage(null);
    updatePropertyInfo(buildUpdateAddressPropertyInfoInput(addressId, data), {
      onError: () => setErrorMessage('Something went wrong'),
      onSuccess: () => void runOnSaved(),
    });
  };

  return (
    <Form methods={formMethods} onSubmit={handleSubmit}>
      <Modal.Body>
        {notice && (
          <Alert colour="yellow" className="mb-4">
            {notice}
          </Alert>
        )}
        <PropertyInfoFields variant={variant} />

        {errorMessage && (
          <Alert colour="red" className="mt-4">
            {errorMessage}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Actions>
          <Button
            type="submit"
            isProcessing={isProcessing}
            disabled={isProcessing}
          >
            {submitLabel}
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Form>
  );
};
